import { AccountBadge } from '@frontend/account/badge';
import { EntityType } from '@frontend/common';
import { ObjectBadge } from '@frontend/components/badge';
import {
    AccountConstraintData,
    ApplicationConstraintData,
    ConstraintData,
    ConstraintType,
    DestinationConstraintData,
    DimensionConstraintData,
    EquipmentConstraintData,
    EquipmentGroupConstraintData,
    EquipmentStateConstraintData,
    FeatureConstraintData,
    FunctionalityConstraintData,
    LocationConstraintData,
    ProductConstraintData,
    ProductGroupConstraintData,
    ProductTypeConstraintData,
    StockConstraintData,
    TransactionCountConstraintData,
    TransactionTypeConstraintData,
    getConstraintDataType
} from '@frontend/constraint/types';
import { DimensionBadge } from '@frontend/elements';
import { EquipmentBadge } from '@frontend/equipment/badge';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { GroupBadge } from '@frontend/group/badge';
import { ProductBadge } from '@frontend/product/badge';
import { SpotBadge } from '@frontend/spot/badge';
import React, { JSX } from 'react';

export interface ConstraintDataMapperProps {
    type?: ConstraintType;
    data: ConstraintData | null;
}
const defaultClassName = 'text-xs text-primary align-content-center text-truncate';
export const ConstraintDataMapper = (props: ConstraintDataMapperProps): JSX.Element => {
    const type = props.type ?? getConstraintDataType(props.data);
    if (!type || !props.data) {
        return <>{JSON.stringify(props.data)}</>;
    }
    switch (type) {
        case ConstraintType.ACCOUNT:
            return <AccountBadge id={(props.data as AccountConstraintData).account_id} />;
        case ConstraintType.APPLICATION:
            return <span className={defaultClassName}>{getApplicationDisplayName((props.data as ApplicationConstraintData).application as Application)}</span>;
        case ConstraintType.DESTINATION:
            return <SpotBadge id={(props.data as DestinationConstraintData).spot_id} />;
        case ConstraintType.DIMENSION:
            return <DimensionBadge dimension={{ type: 'box', height: 0, width: 0, depth: 0, ...(props.data as DimensionConstraintData) }} />;
        case ConstraintType.EQUIPMENT:
            return <EquipmentBadge id={(props.data as EquipmentConstraintData).equipment_id} />;
        case ConstraintType.EQUIPMENT_STATE:
            return <span className={defaultClassName}>{(props.data as EquipmentStateConstraintData).state}</span>;
        case ConstraintType.FEATURE:
            return <span className={defaultClassName}>{(props.data as FeatureConstraintData).feature}</span>;
        case ConstraintType.FUNCTIONALITY:
            return (
                <ObjectBadge
                    entityType={EntityType.FUNCTIONALITY}
                    id={(props.data as FunctionalityConstraintData).functionality_id}
                />
            );
        case ConstraintType.PRODUCT:
            return <ProductBadge id={(props.data as ProductConstraintData).product_id} />;
        case ConstraintType.PRODUCT_TYPE:
            return <span className={defaultClassName}>{(props.data as ProductTypeConstraintData).type}</span>;
        case ConstraintType.STOCK:
            return <span className={defaultClassName}>{(props.data as StockConstraintData).quantity}</span>;
        case ConstraintType.TRANSACTION_COUNT:
            return <span className={defaultClassName}>{(props.data as TransactionCountConstraintData).quantity}</span>;
        case ConstraintType.TRANSACTION_TYPE:
            return <span className={defaultClassName}>{(props.data as TransactionTypeConstraintData).type}</span>;
        case ConstraintType.PRODUCT_GROUP:
            return (
                <GroupBadge
                    id={(props.data as ProductGroupConstraintData).product_group_id}
                    type={EntityType.PRODUCT_GROUP}
                />
            );
        case ConstraintType.EQUIPMENT_GROUP:
            return (
                <GroupBadge
                    id={(props.data as EquipmentGroupConstraintData).equipment_group_id}
                    type={EntityType.EQUIPMENT_GROUP}
                />
            );
        case ConstraintType.LOCATION:
            return <span className={defaultClassName}>{JSON.stringify(props.data as LocationConstraintData)}</span>;
        default:
            return <span className={defaultClassName}>{JSON.stringify(props.data)}</span>;
    }
};

export default ConstraintDataMapper;
